import { useMemo } from "react";
import { eur, lineTotal, fmtQty, pct } from "@/lib/format";

export function QuoteSheet({ quote, client, settings, logoUrl }) {
  const items = useMemo(() => quote.items || [], [quote.items]);
  const hasDiscount = useMemo(() => items.some((it) => (Number(it.discount) || 0) > 0), [items]);
  const disclaimer = settings.disclaimer ||
    "Los precios indicados en este presupuesto están sujetos a posibles variaciones en función del coste de los materiales y de la disponibilidad en el momento de la aprobación. En caso de retraso en la aceptación del presupuesto, los importes podrán ser actualizados para reflejar los cambios de mercado.";
  const thanks = settings.thanks || "Gracias por su confianza.";
  const footer = useMemo(
    () => settings.footer_line || [settings.address, settings.email, settings.phone].filter(Boolean).join(" / "),
    [settings.footer_line, settings.address, settings.email, settings.phone]
  );
  const cols = hasDiscount ? 5 : 4;

  return (
    <div className="bg-white mx-auto max-w-[720px] shadow-md p-8 text-[#1a1a1a]" data-testid="quote-sheet">
      <div className="flex items-start justify-between">
        <div className="h-14 flex items-center">
          {logoUrl ? <img src={logoUrl} alt="logo" className="max-h-14 object-contain" />
                   : <span className="font-display font-bold text-xl">{settings.company_name || "JDMTECH"}</span>}
        </div>
        <div className="font-display font-bold text-3xl sm:text-4xl text-[#4f4f4f]">PRESUPUESTO</div>
      </div>
      <div className="border-t-2 border-[#2f5496] my-3" />
      <div className="flex justify-between text-sm">
        <div>
          <p className="font-bold text-[15px]">{settings.contact_name || settings.company_name}</p>
          {settings.phone && <p className="text-[#666]">TLF: {settings.phone}</p>}
          {settings.email && <p className="text-[#666]">Correo: {settings.email}</p>}
        </div>
        <div className="text-right">
          <p><span className="font-bold">Fecha:</span> {quote.date}</p>
          <p><span className="font-bold">N.º de Presupuesto:</span> N.º {quote.number}</p>
          <p><span className="font-bold">Id. del cliente:</span> {(client?.id || "").slice(0, 8)}</p>
        </div>
      </div>
      <div className="border-t-2 border-[#2f5496] my-3" />
      {quote.kind !== "service" && (
        <div className="flex gap-2 text-sm mb-4">
          <span className="font-bold">Para:</span>
          <div>
            <p>{client?.name || "—"}</p>
            {client?.address && <p>{client.address}</p>}
            {(client?.phone || client?.email) && <p>{[client?.phone, client?.email].filter(Boolean).join(" / ")}</p>}
          </div>
        </div>
      )}

      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="bg-[#4f4f4f] text-white text-[12px]">
            <th className="text-right py-1.5 px-2 font-semibold">Cant.</th>
            <th className="text-left py-1.5 px-2 font-semibold">Descripción</th>
            <th className="text-right py-1.5 px-2 font-semibold">Precio por unidad</th>
            {hasDiscount && <th className="text-right py-1.5 px-2 font-semibold">Descuento</th>}
            <th className="text-right py-1.5 px-2 font-semibold">Total</th>
          </tr>
        </thead>
        <tbody>
          {items.map((it, i) => (
            <tr key={`${i}-${it.description}`} className={i % 2 ? "bg-[#e3eaf5]" : ""}>
              <td className="text-right py-1.5 px-2 align-top">{fmtQty(it.quantity)}</td>
              <td className="text-left py-1.5 px-2 align-top">
                {it.note_above && <p className="text-[11px] italic text-[#666]">{it.note_above}</p>}
                <span>{it.description}</span>
                {it.note_below && <p className="text-[11px] italic text-[#666]">{it.note_below}</p>}
              </td>
              <td className="text-right py-1.5 px-2 align-top">{eur(it.price)}</td>
              {hasDiscount && <td className="text-right py-1.5 px-2 align-top">{pct(it.discount)}</td>}
              <td className="text-right py-1.5 px-2 align-top">{eur(lineTotal(it))}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="flex justify-end items-center gap-4 mt-3 pt-2 border-t border-slate-200">
        <span className="font-display font-bold text-[#2f5496]">Total</span>
        <span className="font-display font-bold bg-[#e3eaf5] px-3 py-1">{eur(quote.total)}</span>
      </div>

      <p className="text-[11px] text-[#666] text-center mt-10 px-4">{disclaimer}</p>
      <p className="font-bold text-center mt-6">{thanks}</p>
      {footer && <p className="text-[12px] text-[#2f5496] text-center mt-1">{footer}</p>}
    </div>
  );
}
